import {
  HTMLAttributes,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'
import useEmblaCarousel from 'embla-carousel-react'

import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import Media, { MediaElement } from '@/components/base/media'
import Section from '@/components/base/section'
import SVGIcon from '@/components/base/svg-icon'

import { cn } from '@/lib/utils'

export type MediaGalleryProps = HTMLAttributes<HTMLElement> &
  Partial<{ items: { media: MediaElement }[] }>

const MediaGallery = ({ items = [], ...props }: MediaGalleryProps) => {
  const [ref, emblaApi] = useEmblaCarousel({ align: 'start' })
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true)
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true)
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([])
  const needsCarousel = useMemo(() => items.length > 1, [items.length])

  const onInit = useCallback(() => {
    if (!emblaApi) return
    setScrollSnaps(emblaApi.scrollSnapList())
  }, [emblaApi])

  const onSelect = useCallback(() => {
    if (!emblaApi) return
    setSelectedIndex(emblaApi.selectedScrollSnap())
    setPrevBtnDisabled(!emblaApi.canScrollPrev())
    setNextBtnDisabled(!emblaApi.canScrollNext())
  }, [emblaApi])

  const handlePrevButtonClick = useCallback(() => {
    if (!emblaApi) return
    emblaApi.scrollPrev()
  }, [emblaApi])

  const handleNextButtonClick = useCallback(() => {
    if (!emblaApi) return
    emblaApi.scrollNext()
  }, [emblaApi])

  const onDotButtonClick = useCallback(
    (index: number) => {
      if (!emblaApi) return
      emblaApi.scrollTo(index)
    },
    [emblaApi]
  )

  useEffect(() => {
    if (!emblaApi) return
    emblaApi.reInit({ active: needsCarousel })
  }, [needsCarousel, emblaApi])

  useEffect(() => {
    if (!emblaApi) return

    onInit()
    onSelect()
    emblaApi.on('reInit', onInit).on('reInit', onSelect).on('select', onSelect)
  }, [emblaApi, onInit, onSelect])

  return (
    <Section {...props}>
      <Container>
        <Grid>
          {items.length > 0 ? (
            <div
              ref={ref}
              className={cn(
                'relative',
                'col-span-4 md:col-span-6 lg:col-span-12',
                '-mx-margin select-none overflow-hidden px-margin lg:mx-0 lg:px-0'
              )}
            >
              <div className="-ml-gutter flex touch-pan-y">
                {items.map(({ media }, index) => (
                  <div
                    key={`media-${index}`}
                    className="min-w-0 shrink-0 grow-0 basis-full pl-gutter"
                  >
                    <div
                      className={cn(
                        'relative aspect-[960/620] bg-havana md:aspect-[1240/620]',
                        'after:absolute after:left-0 after:top-0 after:h-full after:w-full',
                        'after:bg-gradient-to-t after:from-black/30 after:to-black/0 after:to-50%'
                      )}
                    >
                      <Media fill fit="cover" {...media} />
                    </div>
                  </div>
                ))}
              </div>

              {scrollSnaps.length > 1 ? (
                <div
                  className={cn(
                    'absolute bottom-0 left-0 h-auto w-full py-[1.5rem] md:py-[2rem] lg:py-[3rem]',
                    'flex items-center justify-center space-x-[1rem]'
                  )}
                >
                  <button
                    className="-my-[1rem] flex p-[1rem] disabled:opacity-40"
                    disabled={prevBtnDisabled}
                    onClick={handlePrevButtonClick}
                  >
                    <span className="sr-only">Prev Slide</span>
                    <SVGIcon
                      symbol="prev-icon"
                      className="h-[1.3rem] w-[0.7rem] fill-light"
                    />
                  </button>

                  <div className="flex items-center">
                    {scrollSnaps.map((_, index) => (
                      <div key={index} className="flex shrink-0">
                        <button
                          className="-my-[0.5rem] flex p-[0.5rem]"
                          onClick={() => onDotButtonClick(index)}
                        >
                          <span className="sr-only">{`Slide ${index}`}</span>
                          <span
                            className={cn(
                              'relative flex h-[1.5rem] w-[1px]',
                              'before:absolute before:left-0 before:top-0 before:h-full before:w-full before:bg-light/40',
                              'after:absolute after:left-0 after:top-0 after:h-full after:w-full after:origin-bottom after:scale-y-0 after:bg-light',
                              'after:transition-transform after:duration-300 after:ease-in-out-cubic',
                              {
                                'after:scale-y-100': index === selectedIndex
                              }
                            )}
                          />
                        </button>
                      </div>
                    ))}
                  </div>

                  <button
                    className="-my-[1rem] flex p-[1rem] disabled:opacity-40"
                    disabled={nextBtnDisabled}
                    onClick={handleNextButtonClick}
                  >
                    <span className="sr-only">Next Slide</span>
                    <SVGIcon
                      symbol="next-icon"
                      className="h-[1.3rem] w-[0.7rem] fill-light"
                    />
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}
        </Grid>
      </Container>
    </Section>
  )
}

export default MediaGallery
